import React, { useEffect, useState } from 'react';
import API from './api';

const reportTypes = ['daily', 'weekly', 'monthly', 'semiannual'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().slice(0, 10);
};

function MyReports() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [reportType, setReportType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [monthValue, setMonthValue] = useState('');
  const [semiannualValue, setSemiannualValue] = useState('');
  const [summary, setSummary] = useState('');
  const [reports, setReports] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableSemiannual, setAvailableSemiannual] = useState([]);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const res = await API.get('/my-students-skills');
      const studentList = res.data.students_skills.map(s => s.student);
      setStudents(studentList);
    } catch {
      alert('Failed to load students');
    }
  };

  const fetchReports = async (studentId) => {
    try {
      const res = await API.get(`/reports/${studentId}`);
      setReports(res.data);
    } catch {
      alert('Failed to load reports');
    }
  };

  const fetchAvailableMonths = async (studentId) => {
    try {
      const res = await API.get('/reports/available-months', {
        params: { student_id: studentId }
      });
      setAvailableMonths(res.data.available_months || []);
    } catch {
      alert('Failed to fetch available months');
    }
  };
  
  const fetchAvailableSemiannual = async (studentId) => {
    try {
      const res = await API.get('/reports/available-semiannual', {
        params: { student_id: studentId }
      });
      setAvailableSemiannual(res.data || []);
    } catch {
      alert('Failed to fetch semiannual options');
    }
  };

  useEffect(() => {
    if (selectedStudent) {
      fetchReports(selectedStudent);
      fetchAvailableMonths(selectedStudent);
      fetchAvailableSemiannual(selectedStudent);
    }
  }, [selectedStudent]);

  const isWeekday = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDay();
    return day >= 1 && day <= 5;
  };

  const validate = () => {
    if (!selectedStudent) {
      alert('Student must be selected');
      return null;
    }
    if (!reportType) {
      alert('Please select report type');
      return null;
    }
    if (!summary.trim()) {
      alert('Summary cannot be empty');
      return null;
    }

    let from = '';
    let to = '';

    switch (reportType) {
      case 'daily': {
        if (!dateFrom) {
          alert('Date is required');
          return null;
        }
        const formatted = formatDate(dateFrom);
        const day = new Date(formatted).getDay();
        if (day === 0 || day === 6) {
          alert('Selected date is not a weekday');
          return null;
        }
        from = to = formatted;
        break;
      }

      case 'weekly': {
        if (!dateFrom || !dateTo) {
          alert('Both dates required for weekly report');
          return null;
        }
        const formattedFrom = formatDate(dateFrom);
        const formattedTo = formatDate(dateTo);
        const fromDate = new Date(formattedFrom);
        const toDate = new Date(formattedTo);
        const daysDiff = (toDate - fromDate) / (1000 * 60 * 60 * 24) + 1;

        if (daysDiff !== 5) {
          alert('Weekly report must cover exactly 5 days');
          return null;
        }

        for (let i = 0; i < 5; i++) {
          const d = new Date(formattedFrom);
          d.setDate(d.getDate() + i);
          const day = d.getDay();
          if (day === 0 || day === 6) {
            alert('All 5 days must be weekdays');
            return null;
          }
        }

        from = formattedFrom;
        to = formattedTo;
        break;
      }

      case 'monthly': {
        if (!monthValue) {
          alert('Please select a month');
          return null;
        }
        const [year, month] = monthValue.split('-');
        const lastDay = new Date(year, month, 0).getDate();
        from = `${year}-${month}-01`;
        to = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
        break;
      }

      case 'semiannual': {
        if (!semiannualValue) {
          alert('Please select a semiannual period');
          return null;
        }
        [from, to] = semiannualValue.split('|');
        break;
      }

      default:
        return null;
    }

    return { from, to };
  };

  const handleGenerate = async () => {
    const validated = validate();
    if (!validated) return;

    const { from, to } = validated;

    try {
      const res = await API.post('/reports', {
        student_id: selectedStudent,
        type: reportType,
        date_from: from,
        date_to: to,
        summary
      });

      alert(res.data.message || 'Report created');

      // 🔄 Resetuj polja posle uspešnog unosa
      setSummary('');
      setDateFrom('');
      setDateTo('');
      setMonthValue('');
      setSemiannualValue('');
      setReportType('');

      // 🔁 Ponovo učitaj listu izveštaja
      fetchReports(selectedStudent);
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating report');
    }
  };
  
  useEffect(() => {
    setDateFrom('');
    setDateTo('');
    setMonthValue('');
    setSemiannualValue('');
  }, [reportType]);

  // === CARD VIEW ZA MOBILE (do md breakpointa) ===
  const renderMobileCards = () => (
    <div className="flex flex-col gap-3 md:hidden">
      {reports.map(r => (
        <div
          key={r.id}
          className="bg-white rounded-xl shadow p-3 flex flex-col gap-1 border border-gray-200"
        >
          <div className="flex justify-between items-center">
            <div className="font-semibold capitalize">{r.type}</div>
            <span className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</span>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-gray-700">
            <span><b>From:</b> {r.date_from?.slice(0, 10)}</span>
            <span><b>To:</b> {r.date_to?.slice(0, 10)}</span>
          </div>
          <div className="text-xs text-gray-600">
            <b>Summary:</b>
            <br />
            {r.summary || '–'}
          </div>
          <div className="flex justify-end mt-2">
            <button
              className="bg-green-600 text-white text-xs px-3 py-1 rounded"
              onClick={() => window.open(`/report/${r.id}`, '_blank')}
            >
              View
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // === DESKTOP TABLE (od md breakpointa) ===
  const renderDesktopTable = () => (
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full table-auto border text-sm">
        <thead>
          <tr className="bg-gray-200">
            <th className="p-2 border">Type</th>
            <th className="p-2 border">From</th>
            <th className="p-2 border">To</th>
            <th className="p-2 border">Summary</th>
            <th className="p-2 border">Created</th>
            <th className="p-2 border">Print</th>
          </tr>
        </thead>
        <tbody>
          {reports.map(r => (
            <tr key={r.id}>
              <td className="p-2 border">{r.type}</td>
              <td className="p-2 border">{r.date_from?.slice(0, 10)}</td>
              <td className="p-2 border">{r.date_to?.slice(0, 10)}</td>
              <td className="p-2 border">{r.summary || '–'}</td>
              <td className="p-2 border whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
              <td className="p-2 border">
                <button
                  className="bg-green-600 text-white text-xs px-3 py-1 rounded"
                  onClick={() => window.open(`/report/${r.id}`, '_blank')}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-2 sm:p-4">
      <h2 className="text-xl font-bold mb-4">My Reports</h2>

      <div className="space-y-2 sm:space-y-4 mb-4 sm:mb-6">
        {/* Select student */}
        <div>
          <label className="mr-2">Student:</label>
          <select
            className="border px-2 py-1 rounded"
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
          >
            <option value="">-- Choose --</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
            ))}
          </select>
        </div>

        {/* Select report type */}
        {selectedStudent && (
          <div>
            <label className="mr-2">Report Type:</label>
            <select
              className="border px-2 py-1 rounded"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
            >
              <option value="">-- Choose --</option>
              {reportTypes.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Date pickers by type */}
        {reportType === 'daily' && (
          <div>
            <label className="mr-2">Date:</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border px-2 py-1 rounded" />
          </div>
        )}
        {reportType === 'weekly' && (
          <div>
            <label className="mr-2">From:</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border px-2 py-1 rounded" />
            <label className="mx-2">To:</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border px-2 py-1 rounded" />
          </div>
        )}
        {reportType === 'monthly' && (
          <div>
            <label className="mr-2">Month:</label>
            <select value={monthValue} onChange={(e) => setMonthValue(e.target.value)} className="border px-2 py-1 rounded">
              <option value="">-- Select --</option>
              {availableMonths.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        )}
        {reportType === 'semiannual' && (
          <select
            value={semiannualValue}
            onChange={e => setSemiannualValue(e.target.value)}
            className="border p-2"
          >
            <option value="">-- Select Semiannual Period --</option>
            {availableSemiannual.map((item, idx) => (
              <option key={idx} value={`${item.from}|${item.to}`}>
                {item.label}
              </option>
            ))}
          </select>
        )}

        {/* Summary */}
        {reportType && (
          <div>
            <label className="block font-semibold">Summary:</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="w-full border p-2 rounded"
              rows={4}
            />
          </div>
        )}

        {/* Submit button */}
        {reportType && (
          <button
            onClick={handleGenerate}
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            Generate Report
          </button>
        )}
      </div>

      {/* Reports – responsive rendering */}
      {reports.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2">Previous Reports</h3>
          {renderMobileCards()}
          {renderDesktopTable()}
        </div>
      )}
    </div>
  );
}

export default MyReports;





